import { MouseEvent, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  IconButton,
  Popover,
  TextField,
  Tooltip,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { selectMeta, updateMeta } from '../../../redux/meta';
import { useAppDispatch } from '../../../pages/assessment';
import metaScreenConfig from '../../../redux/meta-config/screens';
import { Screens } from '../../../redux/types';
import {
  initialAssessmentState,
  resetAssessmentState,
} from '../../../redux/assessment';
import { selectUser } from '../../../redux/session';
import { devControlsUpdateRecursive } from '../../../redux/helpers/dev-controls';
import { Icon } from '../../icon/icon';

type FormValues = {
  jumpToScreen: Screens;
  overwrite: boolean;
  takeForMinor: boolean;
};

const DevControls = () => {
  const user = useSelector(selectUser);
  const meta = useSelector(selectMeta);
  const dispatch = useAppDispatch();

  const [error, setError] = useState<string>('');
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    formState: { isSubmitting, isValid },
  } = useForm<FormValues>({
    defaultValues: {
      jumpToScreen: null,
      overwrite: false,
      takeForMinor: false,
    },
  });

  const canSkipToScreen = screen => {
    const allowedScreens = [
      Screens.Introduction,
      Screens.GetStarted,
      Screens.PatientType,
      Screens.MedicalInfoIntro,
      Screens.CollectDemographics,
      Screens.ConsentReferrerSharing,
      Screens.SensitiveInformationWarning,
      Screens.Review,
    ];

    if (watch('takeForMinor') === true)
      allowedScreens.push(Screens.GuardianDemographics);

    if (allowedScreens.includes(screen as Screens) || screen.includes('_'))
      return true;

    return false;
  };

  const skipScreens = Object.keys(metaScreenConfig).filter(screen =>
    canSkipToScreen(screen)
  );

  const handleOpen = () => {
    setIsPopoverOpen(true);
  };

  const handleClose = () => {
    setIsPopoverOpen(false);
  };

  const handleFabClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    handleOpen();
  };

  const resetAssessment = () => {
    dispatch(updateMeta({ screen: Screens.Introduction }));
    dispatch(resetAssessmentState(initialAssessmentState));
  };

  const onSubmit: SubmitHandler<FormValues> = async data => {
    try {
      setError('');

      if (data.overwrite) resetAssessment();

      const isPreviousScreen = meta.past.some(
        ({ screen: s }) => s === data.jumpToScreen
      );

      if (isPreviousScreen && !data.overwrite)
        dispatch(updateMeta({ screen: data.jumpToScreen }));
      else {
        await dispatch(
          devControlsUpdateRecursive(data.jumpToScreen, data.takeForMinor)
        );
        setIsPopoverOpen(false);
      }
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <>
      <Tooltip
        title={`Auto-fill intake form…${
          user ? '' : ' (must be authenticated)'
        }`}
        placement="left"
      >
        <Box sx={{ position: 'fixed', top: 70, right: 0, zIndex: 999 }}>
          <IconButton
            disabled={!user}
            onClick={handleFabClick}
            sx={{ m: 1 }}
            role="button"
            aria-label="Dev controls"
          >
            <Icon icon="ClipboardList" />
          </IconButton>
        </Box>
      </Tooltip>
      <Popover open={isPopoverOpen} onClose={handleClose} anchorEl={anchorEl}>
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{ p: 3, position: 'relative' }}
        >
          <Controller
            name="jumpToScreen"
            control={control}
            rules={{
              required: {
                value: true,
                message: 'Select a screen.',
              },
            }}
            render={({ field: { onChange, value: fieldValue, ...field } }) => (
              <Autocomplete
                {...field}
                autoHighlight
                autoSelect
                fullWidth
                options={skipScreens}
                renderInput={params => (
                  <TextField {...params} autoFocus label="Jump to screen" />
                )}
                onChange={(_, data) => onChange(data)}
                sx={{ mb: 1 }}
              />
            )}
          />
          <FormGroup sx={{ mb: 1 }}>
            <Controller
              name="overwrite"
              control={control}
              render={({ field: { onChange, value, ...field } }) => (
                <FormControlLabel
                  {...field}
                  onChange={onChange}
                  checked={value === true}
                  control={<Checkbox />}
                  label="Overwrite current data"
                />
              )}
            />
            <Controller
              name="takeForMinor"
              control={control}
              render={({ field: { onChange, value, ...field } }) => (
                <FormControlLabel
                  {...field}
                  onChange={onChange}
                  checked={value === true}
                  control={<Checkbox />}
                  label="Take for minor (<18 years old)"
                />
              )}
            />
          </FormGroup>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <LoadingButton
            type="submit"
            fullWidth
            variant="contained"
            loading={isSubmitting}
            disabled={isSubmitting || !isValid}
          >
            Apply
          </LoadingButton>
        </Box>
      </Popover>
    </>
  );
};

export default DevControls;
